
import {
  computed,
  defineComponent,
  PropType,
  reactive,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { asInt } from '@/helpers';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import BaseTextInput from '@/components/fields/BaseTextInput.vue';
import ButtonGroup from '@/components/flows/operators/shared/fields/ButtonGroup.vue';

export interface FilterGlobalInformationsData {
  custom_title: string;
  custom_description: string;
  outputs_number: number;
  initial_number_outputs?: null | number;
}

export default defineComponent({
  name: 'FlowFilterGlobalInformations',

  components: {
    BaseTextInput,
    FieldErrors,
    ButtonGroup,
  },

  props: {
    modelValue: {
      type: Object as PropType<FilterGlobalInformationsData>,
      required: true,
    },

    formValidation: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    isEdit: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const formValidationErrors = ref(props.formValidation);
    const componentFieldErrorsKey = ref(0);

    watch(() => props.formValidation, () => {
      formValidationErrors.value = props.formValidation;
      componentFieldErrorsKey.value += 1;
    });

    // Extract the actual validation errors object for BaseTextInput
    // props.formValidation is a Vuelidate ref (v$), so we need v$.value
    const validationErrorsForInput = computed(() => {
      const errors = formValidationErrors.value;
      return errors && errors.value ? errors.value : errors;
    });

    // Use initial_number_outputs from parent if provided, otherwise calculate from current outputs_number
    const initialNumberOutput = props.modelValue.initial_number_outputs ?? asInt(props.modelValue.outputs_number);
    
    const filters = reactive<FilterGlobalInformationsData>({
      custom_title: props.modelValue.custom_title,
      custom_description: props.modelValue.custom_description,
      outputs_number: asInt(props.modelValue.outputs_number),
      initial_number_outputs: props.isEdit ? initialNumberOutput : null,
    });

    const outputOptions = computed(() => [
      {
        value: 1,
        label: t('automatedScenarios.filters.field_outputs.one_output'),
        icon: 'fa-long-arrow-right',
      },
      {
        value: 2,
        label: t('automatedScenarios.filters.field_outputs.two_output'),
        icon: 'fa-code-branch',
      },
    ]);

    const decreasingNumberOfBranches = computed(() => props.isEdit && (filters.outputs_number < initialNumberOutput));

    watch(filters, () => {
      emit('update:modelValue', filters);
    });

    const selectNumberOutputs = (nbrOutputs: number): void => {
      filters.outputs_number = asInt(nbrOutputs);
    };

    const hasError = (fieldName: string) => {
      const errors = validationErrorsForInput.value;
      if (!errors || !errors[fieldName]) return false;
      const fieldErrors = errors[fieldName];
      return (fieldErrors.$errors && fieldErrors.$errors.length > 0) 
        || (fieldErrors.$silentErrors && fieldErrors.$silentErrors.length > 0);
    };

    return {
      t,
      formValidationErrors,
      validationErrorsForInput,
      hasError,
      componentFieldErrorsKey,
      filters,
      selectNumberOutputs,
      decreasingNumberOfBranches,
      outputOptions,
    };
  },
});
